/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteCustomer } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import { getApiErrorReasonMessage } from "../../utils/apiErrorReasons";
import { Modal } from "./ui";
import { buttonClass } from "./styles";

// Confirm before deleting. The red solid button lives only here; everywhere
// else the delete action is outlined.
export default function DeleteCustomerDialog({ open, customer, storeId, onClose, onDeleted }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", storeId] });
      // Only inactive entries: removing the one the open profile observes
      // would refetch it (a 404) before the page navigates away. Active ones
      // go when their page unmounts (gcTime 0).
      queryClient.removeQueries({ queryKey: ["customer", storeId], type: "inactive" });
      setError(null);
      onDeleted?.(customer);
    },
    onError: (err) => {
      setError(
        getApiErrorReasonMessage(err.response?.data, t) ||
          err.response?.data?.error ||
          t("customers.delete.error"),
      );
    },
  });

  const close = () => {
    if (mutation.isPending) return;
    setError(null);
    onClose();
  };

  const name = customer?.name || t("customers.unnamed");

  return (
    <Modal
      open={open}
      onClose={close}
      role="alertdialog"
      title={t("customers.delete.title", { name })}
      footer={
        <>
          <button type="button" onClick={close} className={buttonClass.secondary} data-autofocus>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className={buttonClass.danger}
            disabled={mutation.isPending || !customer}
            onClick={() => mutation.mutate({ storeId, customerId: customer.id })}
          >
            {mutation.isPending ? t("common.deleting") : t("customers.delete.confirm")}
          </button>
        </>
      }
    >
      <ul className="m-0 flex list-disc flex-col gap-1.5 pl-[18px] text-sm leading-relaxed text-gray-700">
        <li>{t("customers.delete.history")}</li>
        <li>{t("customers.delete.wallet")}</li>
        <li>{t("customers.delete.undo")}</li>
      </ul>
      {error && (
        <p role="alert" className="m-0 rounded-[10px] bg-red-50 px-3 py-2.5 text-sm text-red-700">
          {error}
        </p>
      )}
    </Modal>
  );
}
